import { cn } from "@/lib/utils";

interface SkeletonProps {
  /**
   * Number of columns to display
   * @default 1
   */
  columns?: number;
  /**
   * Number of rows to display
   * @default 1
   */
  rows?: number;
  /**
   * Background color variant
   * @default "gray"
   */
  variant?: "gray" | "white";
  /**
   * Skeleton type for different use cases
   * @default "grid"
   */
  type?:
    | "grid"
    | "select_item"
    | "text"
    | "avatar"
    | "button"
    | "input"
    | "textarea"
    | "rectangle"
    | "rounded"
    | "text-line";
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Gap between skeleton items
   * @default "gap-6 gap-y-10"
   */
  gap?: string;
}

export default function Skeleton({
  columns = 1,
  rows = 1,
  variant = "gray",
  type = "grid",
  className,
  gap = "gap-6 gap-y-10",
}: SkeletonProps) {
  const bgColor = variant === "white" ? "bg-white" : "bg-gray-200";

  // Select item skeleton
  if (type === "select_item") {
    return (
      <div className="px-2 py-1.5">
        <div className={cn("h-4 w-24 animate-pulse rounded", bgColor)} />
      </div>
    );
  }

  // Text skeleton
  if (type === "text") {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className={cn("h-4 w-3/4 rounded", bgColor)} />
      </div>
    );
  }

  // Avatar skeleton
  if (type === "avatar") {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className={cn("h-10 w-10 rounded-full", bgColor)} />
      </div>
    );
  }

  // Button skeleton
  if (type === "button") {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className={cn("h-10 w-24 rounded", bgColor)} />
      </div>
    );
  }

  // Input field skeleton
  if (type === "input") {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className={cn("mb-2 h-4 w-24 rounded", bgColor)} />
        <div className={cn("h-11 w-full rounded border bg-gray-50")} />
      </div>
    );
  }

  // Textarea skeleton
  if (type === "textarea") {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className={cn("mb-2 h-4 w-32 rounded", bgColor)} />
        <div className={cn("h-32 w-full rounded border bg-gray-50")} />
      </div>
    );
  }

  // Rectangle skeleton
  if (type === "rectangle") {
    return <div className={cn("animate-pulse", bgColor, className)} />;
  }

  // Rounded skeleton
  if (type === "rounded") {
    return (
      <div className={cn("animate-pulse rounded-lg", bgColor, className)} />
    );
  }

  // Text line skeleton
  if (type === "text-line") {
    return <div className={cn("animate-pulse rounded", bgColor, className)} />;
  }

  // Default grid skeleton
  const gridCols = `grid-cols-${columns}`;

  return (
    <div
      className={cn(
        "lg: grid grid-cols-1 gap-6 gap-y-10",
        gridCols,
        gap,
        className
      )}
    >
      {Array.from({ length: columns * rows }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className={cn("mb-4 h-48 rounded-md", bgColor)}></div>
          <div className={cn("mb-2 h-4 w-3/4 rounded", bgColor)}></div>
          <div className={cn("h-3 w-1/2 rounded", bgColor)}></div>
        </div>
      ))}
    </div>
  );
}
